import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Video, Settings, RefreshCw, AlertTriangle, Camera, Square, Grid2x2, Grid3x3, X, Maximize2, Router, ChevronLeft, ChevronRight, LayoutGrid, MousePointerClick, ExternalLink, Lock, Unlock } from 'lucide-react';
import api from '../../utils/api';
import { CameraLightbox } from '../../components/CameraSnapshot';

export const meta = {
  id: 'cctv_camera',
  category: 'monitoring',
  titleKey: 'dashboard.widgets.cctvCamera',
  descriptionKey: 'dashboard.widgets.cctvCameraDesc',
  defaultSize: 'lg',
  icon: Video,
  minRole: 'admin',
  // Personal cameras (house/office) — pinned to Andy only. No other user, not
  // even a future admin, gets this widget. Server-side per-camera privacy is the
  // real lock; this just keeps the widget out of everyone else's dashboard.
  ownerUserId: 'eafdff80-02d2-4eef-9e5c-19f57761b85d',
};

const REFRESH_OPTIONS = [3, 5, 10, 30];
// layout key -> { cells, cols } for the in-widget camera grid
const LAYOUTS = {
  1: { cells: 1, cols: 1 },
  4: { cells: 4, cols: 2 },
  9: { cells: 9, cols: 3 },
};

// Normalize the persisted config into a stable shape. Backward-compatible with
// the old single-camera config ({ deviceId }).
//
// `mode` decides where the tiles come from:
//   'auto'   — every camera of the scoped company, paged `cells` at a time. This is
//              the default for a company-scoped widget: a client's fleet grows and
//              shrinks on its own, so hand-picking cells would silently go stale
//              (and cap you at 9 of 53, which is exactly what happened with Europa).
//   'manual' — the classic hand-assigned cells. Default when the widget isn't
//              scoped to a company (Andy's personal mixed house/office cameras).
function readConfig(config, companyId) {
  const layout = [1, 4, 9].includes(config?.layout) ? config.layout : 1;
  let deviceIds = Array.isArray(config?.deviceIds) ? config.deviceIds.slice() : [];
  if (deviceIds.length === 0 && config?.deviceId) deviceIds = [config.deviceId];
  const refreshSec = REFRESH_OPTIONS.includes(config?.refreshSec) ? config.refreshSec : 5;
  const mode = config?.mode === 'manual' || config?.mode === 'auto'
    ? config.mode
    : (companyId ? 'auto' : 'manual');
  const page = Number.isInteger(config?.page) && config.page >= 0 ? config.page : 0;
  return { layout, deviceIds, refreshSec, mode, page };
}

// Admin-only live camera tile(s). Browsers can't play RTSP, so we poll a backend
// proxy that grabs a still JPEG from each camera (Dahua/Hikvision) using vault
// creds. Layout (1 / 2x2 / 3x3), the cameras per cell, and the refresh interval
// are stored per-user in the widget's layout config.
// `companyId` (optional) scopes the widget to a single company's cameras — used
// to register a dedicated per-client widget (e.g. "Cámaras Europa"). `titleKey`
// overrides the header label when no single camera is selected.
export default function CctvCameraWidget({ config, onConfigChange, fullscreen, companyId, titleKey, label }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { layout, deviceIds, refreshSec, mode, page } = readConfig(config, companyId);
  const { cells, cols } = LAYOUTS[layout];

  const [cameras, setCameras] = useState(null);   // full camera list (for picker + names)
  const [pickingIndex, setPickingIndex] = useState(null); // which cell is choosing a camera
  const [statuses, setStatuses] = useState(null); // { [deviceId]: online bool } — picker online/offline dots

  // Load the camera list once (used for names + the picker)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = { device_type: 'camera', limit: 200 };
        if (companyId) params.company_id = companyId;
        const { data } = await api.get('/cctv/devices', { params });
        if (!cancelled) setCameras(data?.devices || []);
      } catch {
        if (!cancelled) setCameras([]);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  // When the picker opens, probe online/offline for the dots (lazy + cached server-side)
  useEffect(() => {
    if (pickingIndex === null) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/cctv/status', { params: companyId ? { company_id: companyId } : {} });
        if (!cancelled) setStatuses(data?.statuses || {});
      } catch {
        if (!cancelled) setStatuses({});
      }
    })();
    return () => { cancelled = true; };
  }, [pickingIndex, companyId]);

  // When scoped to a company, watch whether its router/probe still reports to us.
  // Survives the client's archival (endpoint ignores company is_active on purpose).
  const [gateway, setGateway] = useState(null);
  useEffect(() => {
    if (!companyId) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get('/cctv/gateway-status', { params: { company_id: companyId } });
        if (!cancelled) setGateway(data?.probes || []);
      } catch { if (!cancelled) setGateway([]); }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [companyId]);

  // Access audit — dated "do our credentials still work?" history for a company we
  // no longer serve. Only renders when the company is opted in (access_audit_enabled),
  // so active clients never see this strip. Admin-only endpoint.
  const [audit, setAudit] = useState(null);
  useEffect(() => {
    if (!companyId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/cctv/access-audit', { params: { company_id: companyId } });
        if (!cancelled) setAudit(data?.enabled ? (data.runs || []) : null);
      } catch { if (!cancelled) setAudit(null); }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  const camById = useMemo(() => {
    const m = {};
    (cameras || []).forEach(c => { m[c.id] = c; });
    return m;
  }, [cameras]);

  // ── Auto mode: page through the whole (company-scoped) camera list ──
  const autoList = useMemo(() => (mode === 'auto' ? (cameras || []) : []), [mode, cameras]);
  const pageCount = Math.max(1, Math.ceil(autoList.length / cells));
  // Clamp: switching 9 → 1 cells, or a shrinking fleet, can leave `page` past the end.
  const safePage = Math.min(page, pageCount - 1);
  const autoSlice = autoList.slice(safePage * cells, safePage * cells + cells);
  const goPage = (delta) => {
    const next = (safePage + delta + pageCount) % pageCount; // wraps both ways
    onConfigChange?.({ page: next });
  };
  const setMode = (m) => onConfigChange?.({ mode: m, page: 0 });

  const setLayout = (l) => onConfigChange?.({ layout: l, page: 0 });
  const cycleRefresh = () => {
    const idx = REFRESH_OPTIONS.indexOf(refreshSec);
    onConfigChange?.({ refreshSec: REFRESH_OPTIONS[(idx + 1) % REFRESH_OPTIONS.length] });
  };
  const assignCamera = (index, id) => {
    const next = deviceIds.slice();
    while (next.length <= index) next.push(null);
    next[index] = id;
    onConfigChange?.({ deviceIds: next, deviceId: undefined });
    setPickingIndex(null);
  };
  const clearCamera = (index) => {
    const next = deviceIds.slice();
    if (index < next.length) next[index] = null;
    onConfigChange?.({ deviceIds: next });
  };

  const singleName = layout !== 1 ? null
    : mode === 'auto' ? (autoSlice[0]?.name || null)
    : (deviceIds[0] ? camById[deviceIds[0]]?.name : null);

  // ── Camera picker overlay (for a specific cell) ──
  if (pickingIndex !== null) {
    const chosen = new Set(deviceIds.filter(Boolean));
    return (
      <div className="card" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Camera size={16} /> {t('dashboard.widgets.cctvPick')}
          </div>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setPickingIndex(null)} title={t('common.cancel', 'Cancelar')}>
            <X size={14} />
          </button>
        </div>
        <div style={{ padding: '8px 12px 12px', flex: 1, minHeight: 0 }}>
          {cameras === null ? (
            <div style={{ fontSize: 13, color: 'var(--c-text-muted)' }}>{t('common.loading', 'Cargando…')}</div>
          ) : cameras.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--c-text-muted)' }}>{t('dashboard.widgets.cctvNoCameras')}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: fullscreen ? 520 : 260, overflowY: 'auto' }}>
              {cameras.map(cam => (
                <button
                  key={cam.id}
                  onClick={() => assignCamera(pickingIndex, cam.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left',
                    padding: '9px 12px', borderRadius: 10,
                    background: 'var(--c-surface-2)', border: '1px solid var(--c-border)',
                    cursor: 'pointer', color: 'var(--c-text)', opacity: chosen.has(cam.id) ? 0.5 : 1,
                  }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--c-brand)')}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--c-border)')}
                >
                  <div style={{
                    width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                    background: 'rgba(59,130,246,0.12)', color: 'var(--c-brand)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Camera size={16} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {cam.name}{chosen.has(cam.id) ? ` · ${t('dashboard.widgets.cctvInUse', 'en uso')}` : ''}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--c-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {cam.company_name}{cam.location_name ? ` · ${cam.location_name}` : ''}
                    </div>
                  </div>
                  {/* Online/offline dot (TCP reachability) */}
                  {(() => {
                    const st = statuses ? statuses[cam.id] : null;
                    const color = st === true ? '#22c55e' : st === false ? '#ef4444' : 'var(--c-border)';
                    const title = st === true ? t('dashboard.widgets.cctvOnline', 'Online')
                      : st === false ? t('dashboard.widgets.cctvOffline', 'Offline')
                      : (statuses ? t('dashboard.widgets.cctvUnknown', 'Sin host / desconocido') : t('dashboard.widgets.cctvProbing', 'Probando…'));
                    return (
                      <span title={title} style={{ marginLeft: 'auto', flexShrink: 0, paddingLeft: 8 }}>
                        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, boxShadow: st === true ? '0 0 5px #22c55e' : 'none' }} />
                      </span>
                    );
                  })()}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  const minBodyHeight = fullscreen ? 520 : (layout === 1 ? 180 : 220);

  return (
    <div className="card" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <Video size={16} style={{ flexShrink: 0 }} />
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {singleName || label || t(titleKey || 'dashboard.widgets.cctvCamera')}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          {/* Auto (todas, paginadas) vs manual (celdas elegidas a mano) */}
          {companyId && (
            <div style={{ display: 'flex', gap: 2, marginRight: 2 }}>
              <LayoutBtn active={mode === 'auto'} onClick={() => setMode('auto')} title={t('dashboard.widgets.cctvModeAuto')}><LayoutGrid size={13} /></LayoutBtn>
              <LayoutBtn active={mode === 'manual'} onClick={() => setMode('manual')} title={t('dashboard.widgets.cctvModeManual')}><MousePointerClick size={13} /></LayoutBtn>
            </div>
          )}
          {/* Layout switcher: 1 / 2x2 / 3x3 */}
          <div style={{ display: 'flex', gap: 2, marginRight: 2 }}>
            <LayoutBtn active={layout === 1} onClick={() => setLayout(1)} title={t('dashboard.widgets.cctvLayout1', 'Una cámara')}><Square size={13} /></LayoutBtn>
            <LayoutBtn active={layout === 4} onClick={() => setLayout(4)} title={t('dashboard.widgets.cctvLayout4', 'Grilla de 4')}><Grid2x2 size={13} /></LayoutBtn>
            <LayoutBtn active={layout === 9} onClick={() => setLayout(9)} title={t('dashboard.widgets.cctvLayout9', 'Grilla de 9')}><Grid3x3 size={13} /></LayoutBtn>
          </div>
          <button className="btn btn-ghost btn-icon btn-sm" title={`${refreshSec}s`} onClick={cycleRefresh}
            style={{ fontSize: 10, fontWeight: 700, minWidth: 30 }}>
            {refreshSec}s
          </button>
        </div>
      </div>

      {/* Router/probe reporting strip — only for a company-scoped widget */}
      {companyId && Array.isArray(gateway) && gateway.length > 0 && (() => {
        const p = gateway[0];
        const secs = p.secs_ago == null ? null : Number(p.secs_ago);
        const online = (p.heartbeat_status === 'online') || (secs != null && secs < 900);
        let ago = null;
        if (secs != null) {
          ago = secs < 90 ? t('dashboard.widgets.routerJustNow', 'recién')
            : secs < 3600 ? t('dashboard.widgets.routerAgoM', { n: Math.round(secs / 60), defaultValue: `hace ${Math.round(secs / 60)}m` })
            : secs < 86400 ? t('dashboard.widgets.routerAgoH', { n: Math.round(secs / 3600), defaultValue: `hace ${Math.round(secs / 3600)}h` })
            : t('dashboard.widgets.routerAgoD', { n: Math.round(secs / 86400), defaultValue: `hace ${Math.round(secs / 86400)}d` });
        }
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 12px 6px', fontSize: 11, color: 'var(--c-text-muted)' }}>
            <Router size={13} style={{ color: online ? '#22c55e' : '#ef4444', flexShrink: 0 }} />
            <span style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: online ? '#22c55e' : '#ef4444', boxShadow: online ? '0 0 5px #22c55e' : 'none' }} />
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {online ? t('dashboard.widgets.routerReporting', 'Router reportando') : t('dashboard.widgets.routerSilent', 'Router sin reporte')}
              {ago ? ` · ${ago}` : ''}
            </span>
          </div>
        );
      })()}

      {/* Access-audit timeline — the dated evidence of how long our old credentials
          kept working after the client left. Reads oldest → newest, left to right. */}
      {Array.isArray(audit) && audit.length > 0 && (() => {
        const last = audit[audit.length - 1];
        const stillIn = last?.had_access === true;
        return (
          <div style={{ padding: '0 12px 6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              {stillIn ? <Unlock size={13} style={{ color: '#f59e0b', flexShrink: 0 }} />
                       : <Lock size={13} style={{ color: '#22c55e', flexShrink: 0 }} />}
              <span style={{ fontSize: 11, color: 'var(--c-text-muted)', fontWeight: 600 }}>
                {stillIn
                  ? t('dashboard.widgets.cctvAuditStillIn', { n: audit.filter(r => r.had_access).length })
                  : t('dashboard.widgets.cctvAuditClosed')}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {audit.map((r) => {
                const d = new Date(r.run_at);
                const label = `${d.getDate()}/${d.getMonth() + 1}`;
                const ok = r.had_access === true;
                return (
                  <span
                    key={r.run_at}
                    title={`${d.toLocaleDateString()} — ${ok
                      ? t('dashboard.widgets.cctvAuditOk', { ok: r.targets_ok, total: r.targets })
                      : t('dashboard.widgets.cctvAuditNo')}`}
                    style={{
                      fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 999,
                      background: ok ? 'rgba(245,158,11,0.16)' : 'rgba(34,197,94,0.16)',
                      color: ok ? '#f59e0b' : '#22c55e',
                      border: `1px solid ${ok ? 'rgba(245,158,11,0.35)' : 'rgba(34,197,94,0.35)'}`,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {label}
                  </span>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Pager — auto mode only, and only when the fleet doesn't fit in one page */}
      {mode === 'auto' && pageCount > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '0 12px 6px' }}>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => goPage(-1)} title={t('dashboard.widgets.cctvPrevPage')}>
            <ChevronLeft size={15} />
          </button>
          <span style={{ fontSize: 11, color: 'var(--c-text-muted)', fontWeight: 600, minWidth: 90, textAlign: 'center' }}>
            {t('dashboard.widgets.cctvPageOf', {
              from: safePage * cells + 1,
              to: Math.min((safePage + 1) * cells, autoList.length),
              total: autoList.length,
            })}
          </span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => goPage(1)} title={t('dashboard.widgets.cctvNextPage')}>
            <ChevronRight size={15} />
          </button>
        </div>
      )}

      <div style={{
        flex: 1, minHeight: minBodyHeight, margin: '0 12px 12px',
        display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gridAutoRows: '1fr', gap: cols > 1 ? 6 : 0,
      }}>
        {mode === 'auto' ? (
          cameras === null ? (
            <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--c-text-muted)' }}>
              {t('common.loading', 'Cargando…')}
            </div>
          ) : autoList.length === 0 ? (
            <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--c-text-muted)' }}>
              {t('dashboard.widgets.cctvNoCameras')}
            </div>
          ) : (
            autoSlice.map(cam => (
              <CameraCell
                key={cam.id}
                device={cam}
                deviceId={cam.id}
                refreshSec={refreshSec}
                compact={layout !== 1}
                onOpenModule={() => navigate(`/cctv?id=${cam.id}`)}
              />
            ))
          )
        ) : (
          Array.from({ length: cells }).map((_, i) => {
            const id = deviceIds[i] || null;
            return id ? (
              <CameraCell
                key={`${id}-${i}`}
                device={camById[id] || null}
                deviceId={id}
                refreshSec={refreshSec}
                compact={layout !== 1}
                onOpenModule={() => navigate(`/cctv?id=${id}`)}
                onChange={() => setPickingIndex(i)}
                onRemove={() => clearCamera(i)}
              />
            ) : (
              <EmptyCell key={`empty-${i}`} onClick={() => setPickingIndex(i)} t={t} />
            );
          })
        )}
      </div>
    </div>
  );
}

function LayoutBtn({ active, onClick, title, children }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className="btn btn-icon btn-sm"
      style={{
        background: active ? 'var(--c-brand)' : 'transparent',
        color: active ? '#fff' : 'var(--c-text-muted)',
        border: 'none',
      }}
    >
      {children}
    </button>
  );
}

function EmptyCell({ onClick, t }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', height: '100%', minHeight: 80,
        background: 'var(--c-surface-2)', border: '1px dashed var(--c-border)',
        borderRadius: 8, cursor: 'pointer', color: 'var(--c-text-muted)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
      }}
      onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--c-brand)')}
      onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--c-border)')}
    >
      <Camera size={20} />
      <span style={{ fontSize: 11, fontWeight: 600 }}>{t('dashboard.widgets.cctvAddCamera', 'Agregar cámara')}</span>
    </button>
  );
}

// One live camera cell — polls its own snapshot on the shared interval.
// Clicking the frame opens it FULLSCREEN (the lightbox); jumping to the CCTV
// module moved to its own hover button, so the common action (look closer) is
// the one that doesn't navigate away from the dashboard.
// `onChange`/`onRemove` only exist in manual mode — in auto mode the cells are
// derived from the company's fleet, so there is nothing to pick or clear.
function CameraCell({ device, deviceId, refreshSec, compact, onOpenModule, onChange, onRemove }) {
  const { t } = useTranslation();
  const [imgUrl, setImgUrl] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hover, setHover] = useState(false);
  const [fs, setFs] = useState(false);
  const objectUrlRef = useRef(null);
  const timerRef = useRef(null);

  const fetchSnapshot = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get(`/cctv/devices/${deviceId}/snapshot`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = url;
      setImgUrl(url);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.code || 'CAMERA_UNREACHABLE');
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    fetchSnapshot();
    timerRef.current = setInterval(fetchSnapshot, Math.max(3, refreshSec) * 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fetchSnapshot, refreshSec]);

  useEffect(() => () => { if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current); }, []);

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative', width: '100%', height: '100%', minHeight: 80,
        background: '#000', borderRadius: 8, overflow: 'hidden',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div onClick={() => { if (imgUrl && !err) setFs(true); }} style={{ position: 'absolute', inset: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {imgUrl && !err ? (
          <img src={imgUrl} alt={device?.name || 'camera'} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        ) : err ? (
          <div style={{ textAlign: 'center', color: 'var(--c-text-muted)', padding: 12 }}>
            <AlertTriangle size={compact ? 18 : 24} style={{ color: '#f59e0b', marginBottom: 6 }} />
            <div style={{ fontSize: compact ? 10 : 12 }}>
              {err === 'NO_HOST' ? t('dashboard.widgets.cctvNoHost') : t('dashboard.widgets.cctvUnreachable')}
            </div>
          </div>
        ) : (
          <div style={{ color: 'var(--c-text-muted)', fontSize: 11 }}>{t('common.loading', 'Cargando…')}</div>
        )}
      </div>

      {/* LIVE badge */}
      {imgUrl && !err && (
        <div style={{
          position: 'absolute', top: 6, left: 6, display: 'flex', alignItems: 'center', gap: 4,
          background: 'rgba(0,0,0,0.55)', borderRadius: 999, padding: '2px 6px', pointerEvents: 'none',
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444', animation: 'pulse 1.4s ease-in-out infinite' }} />
          <span style={{ fontSize: 9, fontWeight: 700, color: '#fff', letterSpacing: '0.5px' }}>LIVE</span>
        </div>
      )}

      {/* Camera name overlay (bottom-left) */}
      {device?.name && (
        <div style={{
          position: 'absolute', bottom: 6, left: 6, maxWidth: '70%',
          background: 'rgba(0,0,0,0.55)', borderRadius: 6, padding: '2px 7px',
          fontSize: compact ? 9 : 11, color: '#fff', fontWeight: 600,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', pointerEvents: 'none',
        }}>
          {device.name}
        </div>
      )}

      {/* Hover controls: fullscreen / change / remove / refresh */}
      {hover && (
        <div style={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 4, zIndex: 3 }}>
          {imgUrl && !err && (
            <button className="btn btn-icon btn-sm" title={t('cctv.fullscreen.open')}
              onClick={(e) => { e.stopPropagation(); setFs(true); }}
              style={{ background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', width: 24, height: 24 }}>
              <Maximize2 size={12} />
            </button>
          )}
          {onOpenModule && (
            <button className="btn btn-icon btn-sm" title={t('dashboard.widgets.cctvOpenInModule')}
              onClick={(e) => { e.stopPropagation(); onOpenModule(); }}
              style={{ background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', width: 24, height: 24 }}>
              <ExternalLink size={12} />
            </button>
          )}
          {onChange && (
            <button className="btn btn-icon btn-sm" title={t('dashboard.widgets.cctvChange')}
              onClick={(e) => { e.stopPropagation(); onChange(); }}
              style={{ background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', width: 24, height: 24 }}>
              <Settings size={12} />
            </button>
          )}
          {onRemove && (
            <button className="btn btn-icon btn-sm" title={t('common.remove', 'Quitar')}
              onClick={(e) => { e.stopPropagation(); onRemove(); }}
              style={{ background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', width: 24, height: 24 }}>
              <X size={12} />
            </button>
          )}
          <button className="btn btn-icon btn-sm" title={t('common.refresh', 'Refrescar')}
            onClick={(e) => { e.stopPropagation(); fetchSnapshot(); }}
            style={{ background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', width: 24, height: 24 }}>
            <RefreshCw size={12} style={{ animation: loading ? 'spin 0.8s linear infinite' : 'none' }} />
          </button>
        </div>
      )}

      {fs && <CameraLightbox deviceId={deviceId} name={device?.name} onClose={() => setFs(false)} />}
    </div>
  );
}
